import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapTrifold, Plus, Trash, X, Pause, Play, CalendarBlank, Info } from "@phosphor-icons/react";

const WEEKDAYS = [
  { v: 0, l: "Montag" }, { v: 1, l: "Dienstag" }, { v: 2, l: "Mittwoch" },
  { v: 3, l: "Donnerstag" }, { v: 4, l: "Freitag" }, { v: 5, l: "Samstag" }, { v: 6, l: "Sonntag" },
];
const FREQS = [
  { v: "weekly", l: "Wöchentlich" },
  { v: "biweekly", l: "14-täglich" },
  { v: "monthly", l: "Monatlich" },
];

export default function ServiceZonesPanel({ tenant }) {
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!tenant?.tenant_id) return;
    api.get(`/tenants/${tenant.tenant_id}/zones`).then((r) => { setZones(r.data.zones || []); setDirty(false); });
  }, [tenant?.tenant_id]);

  const addZone = () => {
    setZones([...zones, {
      zone_id: `zone_${Math.random().toString(36).slice(2, 10)}`,
      name: "",
      zips: [],
      pickup_weekday: 1,
      frequency: "weekly",
      signup_stopped: false,
      stop_reason: null,
    }]);
    setDirty(true);
  };
  const updateZone = (i, patch) => {
    const next = [...zones];
    next[i] = { ...next[i], ...patch };
    setZones(next);
    setDirty(true);
  };
  const removeZone = (i) => { setZones(zones.filter((_, ix) => ix !== i)); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/zones`, { zones });
      setZones(r.data.zones || []);
      setDirty(false);
      setMsg({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setBusy(false); }
  };

  const toggleStop = async (i) => {
    const z = zones[i];
    if (!z.zone_id.startsWith("zone_")) return;
    const nextStopped = !z.signup_stopped;
    const reason = nextStopped ? (window.prompt("Grund für Aufnahmestopp (optional):") || "") : "";
    try {
      // If zone is unsaved, just update local state
      if (dirty) {
        updateZone(i, { signup_stopped: nextStopped, stop_reason: reason });
        return;
      }
      await api.post(`/tenants/${tenant.tenant_id}/zones/${z.zone_id}/signup-stop`, { stopped: nextStopped, reason });
      const next = [...zones];
      next[i] = { ...z, signup_stopped: nextStopped, stop_reason: reason || null };
      setZones(next);
      setMsg({ ok: true, msg: nextStopped ? "Aufnahmestopp aktiv" : "Wieder aufnahmebereit" });
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    }
  };

  return (
    <div className="space-y-5" data-testid="service-zones-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <MapTrifold size={22} weight="bold" /> Müllbezirke & Aufnahmestopp
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Bezirke mit fester Abholung — Neukunden werden automatisch dem passenden Bezirk zugeordnet und bekommen das nächste Datum als Erst-Reinigung.
          </p>
        </div>
        {msg && <span className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</span>}
      </div>

      <div className="flex items-start gap-2 p-3 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
        <Info size={14} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Aufnahmestopp:</strong> Sobald ein Bezirk pausiert ist, sehen Neukunden bei der Buchung eine „Bald wieder verfügbar"-Seite und landen automatisch in der Interessentenliste.
          Bestandskunden sind davon nicht betroffen.
        </div>
      </div>

      <div className="space-y-3">
        {zones.length === 0 ? (
          <div className="text-sm text-zinc-400 italic p-6 bg-zinc-50 border border-black/5 text-center rounded">
            Noch keine Bezirke — legen Sie den ersten Müllbezirk an.
          </div>
        ) : zones.map((z, i) => (
          <article key={z.zone_id} className={`rounded border p-4 ${z.signup_stopped ? "bg-red-50 border-red-200" : "bg-white border-black/10"}`} data-testid={`zone-item-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_150px_auto] gap-3 items-end">
              <div>
                <Label className="text-xs">Bezirks-Name</Label>
                <Input
                  className="h-10 rounded-none"
                  value={z.name}
                  onChange={(e) => updateZone(i, { name: e.target.value })}
                  placeholder="z.B. Nord-Ost / Kaiserring"
                  data-testid={`zone-name-${i}`}
                />
              </div>
              <div>
                <Label className="text-xs">Abhol-Tag</Label>
                <Select value={String(z.pickup_weekday ?? 1)} onValueChange={(v) => updateZone(i, { pickup_weekday: parseInt(v) })}>
                  <SelectTrigger className="h-10 rounded-none" data-testid={`zone-weekday-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.v} value={String(w.v)}>{w.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Frequenz</Label>
                <Select value={z.frequency || "weekly"} onValueChange={(v) => updateZone(i, { frequency: v })}>
                  <SelectTrigger className="h-10 rounded-none" data-testid={`zone-freq-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={() => toggleStop(i)}
                  className={`h-10 px-3 rounded text-xs font-bold flex items-center gap-1.5 ${z.signup_stopped ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}`}
                  data-testid={`zone-toggle-stop-${i}`}
                  title={z.signup_stopped ? "Aufnahme wieder freigeben" : "Aufnahme stoppen"}
                >
                  {z.signup_stopped ? <><Play size={12} weight="bold" /> Freigeben</> : <><Pause size={12} weight="bold" /> Stop</>}
                </button>
                <button
                  onClick={() => removeZone(i)}
                  className="h-10 w-10 rounded hover:bg-red-100 text-red-600 flex items-center justify-center"
                  data-testid={`zone-remove-${i}`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>

            <div className="mt-3">
              <Label className="text-xs">PLZs in diesem Bezirk (kommagetrennt)</Label>
              <Input
                className="h-10 rounded-none font-mono text-sm"
                value={(z.zips || []).join(", ")}
                onChange={(e) => updateZone(i, { zips: e.target.value.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean) })}
                placeholder="79576, 79541, 79540"
                data-testid={`zone-zips-${i}`}
              />
            </div>

            {z.signup_stopped && (
              <div className="mt-3 flex items-start gap-2 p-2 rounded bg-red-100 text-xs text-red-800">
                <Pause size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Aufnahmestopp aktiv.</strong> {z.stop_reason ? `Grund: ${z.stop_reason}` : "Neukunden werden zur Coming-Soon-Seite umgeleitet."}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={addZone} variant="outline" className="rounded-none h-10" data-testid="zone-add-btn">
          <Plus size={13} weight="bold" className="mr-1" /> Bezirk hinzufügen
        </Button>
        <Button onClick={save} disabled={!dirty || busy} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 disabled:opacity-50" data-testid="zones-save-btn">
          {busy ? "Speichere…" : dirty ? "Änderungen speichern" : "Gespeichert"}
        </Button>
      </div>
    </div>
  );
}
